<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import SegmentedToggle from 'dashboard/components-next/segmented-toggle/SegmentedToggle.vue';
import { OnClickOutside } from '@vueuse/components';

const props = defineProps({
  currentPage: {
    type: Number,
    required: true,
  },
  totalItems: {
    type: Number,
    required: true,
  },
  itemsPerPage: {
    type: Number,
    default: 20,
  },
  isSchemaActive: {
    type: Boolean,
    default: false,
  },
  showSchemaToggle: {
    type: Boolean,
    default: true,
  },
  processingCount: {
    type: Number,
    default: 0,
  },
  processingRecords: {
    type: Array,
    default: () => [],
  },
  errorCount: {
    type: Number,
    default: 0,
  },
  errorRecords: {
    type: Array,
    default: () => [],
  },
  isUploading: {
    type: Boolean,
    default: false,
  },
  uploadProgress: {
    type: Number,
    default: 0,
  },
  uploadStats: {
    type: Object,
    default: () => ({ total: 0, completed: 0, failed: 0, inProgress: 0 }),
  },
});

const emit = defineEmits([
  'update:currentPage',
  'toggleSchema',
  'retryFailedRecords',
  'deleteProcessingRecords',
  'deleteFailedRecords',
  'openSitemaps',
]);

const { t } = useI18n();

const showProcessingMenu = ref(false);
const showErrorMenu = ref(false);

const totalPages = computed(() =>
  Math.ceil(props.totalItems / props.itemsPerPage)
);

const canGoPrev = computed(() => props.currentPage > 1);
const canGoNext = computed(() => props.currentPage < totalPages.value);

const startItem = computed(() => {
  if (props.totalItems === 0) return 0;
  return (props.currentPage - 1) * props.itemsPerPage + 1;
});

const endItem = computed(() => {
  const end = props.currentPage * props.itemsPerPage;
  return Math.min(end, props.totalItems);
});

const currentPageInformation = computed(() => {
  return t('PAGINATION_FOOTER.SHOWING', {
    startItem: startItem.value,
    endItem: endItem.value,
    totalItems: props.totalItems,
  });
});

const pageInfo = computed(() => {
  return t('PAGINATION_FOOTER.CURRENT_PAGE_INFO', {
    currentPage: '',
    totalPages: totalPages.value,
  });
});

const handlePrevPage = () => {
  if (canGoPrev.value) {
    emit('update:currentPage', props.currentPage - 1);
  }
};

const handleNextPage = () => {
  if (canGoNext.value) {
    emit('update:currentPage', props.currentPage + 1);
  }
};

const handleFirstPage = () => {
  if (props.currentPage > 1) {
    emit('update:currentPage', 1);
  }
};

const handleLastPage = () => {
  if (props.currentPage < totalPages.value) {
    emit('update:currentPage', totalPages.value);
  }
};

const handleRetryFailed = () => {
  emit('retryFailedRecords', props.errorRecords);
  showErrorMenu.value = false;
};

const handleRetrySingle = record => {
  emit('retryFailedRecords', [record]);
};

const handleDeleteFailed = () => {
  emit('deleteFailedRecords', props.errorRecords);
  showErrorMenu.value = false;
};

const handleDeleteProcessing = () => {
  emit('deleteProcessingRecords', props.processingRecords);
  showProcessingMenu.value = false;
};

// Show unified progress indicator for both uploading and processing
const showProgressIndicator = computed(() => {
  // Only show upload indicator if there are actual uploads in the queue
  const hasUploads = props.uploadStats.total > 0;
  return (props.isUploading && hasUploads) || props.processingCount > 0;
});

const progressLabel = computed(() => {
  if (props.isUploading && props.uploadStats.total > 0) {
    return `Uploading ${props.uploadStats.completed + props.uploadStats.inProgress}/${props.uploadStats.total}`;
  }
  return t('LIBRARIES.DOCUMENTS.STATUS.PROCESSING', {
    count: props.processingCount,
  });
});

const progressIcon = computed(() => {
  if (props.isUploading && props.uploadStats.total > 0) {
    return 'i-lucide-upload';
  }
  return 'i-lucide-loader-circle';
});

const schemaToggleOptions = computed(() => [
  {
    value: false,
    label: 'Data',
    icon: 'i-lucide-database',
  },
  {
    value: true,
    label: 'Schema',
    icon: 'i-lucide-settings',
  },
]);
</script>

<template>
  <div
    class="w-full bg-white dark:bg-slate-900 border-t border-slate-200 dark:border-slate-700 px-4 py-3 flex items-center justify-between align-baseline"
  >
    <div class="flex items-center space-x-4">
      <!-- Data Sources Button -->
      <NextButton
        icon="i-lucide-database"
        variant="ghost"
        size="sm"
        color="slate"
        @click="emit('openSitemaps')"
      >
        {{ t('LIBRARIES.SITEMAPS_MODAL.BUTTON_LABEL') }}
      </NextButton>

      <!-- Unified Progress Indicator (Uploading → Processing) -->
      <div
        v-if="showProgressIndicator"
        class="flex items-center gap-2 px-3 py-1.5 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md min-w-[320px]"
      >
        <Icon
          :icon="progressIcon"
          class="w-4 h-4 text-blue-600 dark:text-blue-400 flex-shrink-0"
          :class="{
            'animate-pulse': isUploading && uploadStats.total > 0,
            'animate-spin': !isUploading || uploadStats.total === 0,
          }"
        />
        <span class="text-sm text-blue-700 dark:text-blue-300">
          {{ progressLabel }}
        </span>
        <div class="flex items-center gap-2 ml-auto">
          <div
            v-if="isUploading && uploadStats.total > 0"
            class="w-24 h-1.5 bg-blue-100 dark:bg-blue-900 rounded-full overflow-hidden"
          >
            <div
              class="h-full bg-blue-500 dark:bg-blue-400 transition-all duration-300"
              :style="{ width: `${uploadProgress}%` }"
            />
          </div>
          <span
            v-if="isUploading && uploadStats.total > 0"
            class="text-xs text-blue-600 dark:text-blue-400 font-medium min-w-[32px] text-right"
          >
            {{ uploadProgress }}%
          </span>
          <NextButton
            v-if="!isUploading && processingCount > 0"
            variant="ghost"
            size="sm"
            icon="i-lucide-trash-2"
            color="ruby"
            @click="showProcessingMenu = !showProcessingMenu"
          />
        </div>
      </div>

      <!-- Status Pills (left-aligned) -->
      <div class="flex items-center space-x-2">
        <!-- Processing Menu Dropdown (hidden, triggered by button above) -->
        <div v-if="processingCount > 0 && !isUploading" class="relative">
          <OnClickOutside @trigger="showProcessingMenu = false">
            <div
              v-if="showProcessingMenu"
              class="absolute bottom-full mb-2 ltr:left-0 rtl:right-0 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg p-3 z-50 min-w-[300px] max-w-[400px]"
            >
              <div
                class="text-sm font-medium text-slate-700 dark:text-slate-300 mb-3"
              >
                {{
                  t(
                    'LIBRARIES.DOCUMENTS.STATUS.PROCESSING_MESSAGE',
                    processingCount
                  )
                }}
              </div>
              <div class="max-h-[200px] overflow-y-auto mb-3 space-y-2">
                <div
                  v-for="record in processingRecords"
                  :key="record.id"
                  class="text-xs text-slate-600 dark:text-slate-400 p-2 bg-slate-50 dark:bg-slate-900 rounded border border-slate-200 dark:border-slate-700"
                >
                  <div class="font-medium truncate">
                    {{ record.name || 'Unnamed Record' }}
                  </div>
                  <div class="text-slate-500 dark:text-slate-500 capitalize">
                    {{ record.status }}
                  </div>
                </div>
              </div>
              <div class="flex gap-2">
                <NextButton
                  icon="i-lucide-trash-2"
                  variant="solid"
                  color="ruby"
                  size="sm"
                  @click="handleDeleteProcessing"
                >
                  {{ t('LIBRARIES.DOCUMENTS.STATUS.DELETE_PROCESSING') }}
                </NextButton>
              </div>
            </div>
          </OnClickOutside>
        </div>

        <!-- Error Status Pill with Dropdown (only when not showing progress) -->
        <div v-if="errorCount > 0 && !showProgressIndicator" class="relative">
          <OnClickOutside @trigger="showErrorMenu = false">
            <NextButton
              variant="ghost"
              size="sm"
              @click="showErrorMenu = !showErrorMenu"
            >
              <Icon icon="i-lucide-alert-circle" class="w-4 h-4" />
              {{
                t('LIBRARIES.DOCUMENTS.STATUS.FAILED', { count: errorCount })
              }}
            </NextButton>
            <div
              v-if="showErrorMenu"
              class="absolute bottom-full mb-2 ltr:left-0 rtl:right-0 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg p-3 z-50 min-w-[300px] max-w-[400px]"
            >
              <div
                class="text-sm font-medium text-slate-700 dark:text-slate-300 mb-3"
              >
                {{ t('LIBRARIES.DOCUMENTS.STATUS.FAILED_MESSAGE', errorCount) }}
              </div>
              <div class="max-h-[200px] overflow-y-auto mb-3 space-y-2">
                <div
                  v-for="record in errorRecords"
                  :key="record.id"
                  class="text-xs text-slate-600 dark:text-slate-400 p-2 bg-slate-50 dark:bg-slate-900 rounded border border-slate-200 dark:border-slate-700 flex items-center justify-between"
                >
                  <div class="flex-1 min-w-0">
                    <div class="font-medium truncate">
                      {{ record.name || 'Unnamed Record' }}
                    </div>
                    <div class="text-slate-500 dark:text-slate-500 capitalize">
                      {{ record.status }}
                    </div>
                  </div>
                  <NextButton
                    icon="i-lucide-refresh-cw"
                    variant="ghost"
                    size="sm"
                    class="ml-2 flex-shrink-0"
                    @click="handleRetrySingle(record)"
                  />
                </div>
              </div>
              <div class="flex gap-2">
                <NextButton
                  icon="i-lucide-refresh-cw"
                  variant="ghost"
                  size="sm"
                  @click="handleRetryFailed"
                >
                  {{ t('LIBRARIES.DOCUMENTS.STATUS.RETRY_ALL') }}
                </NextButton>
                <NextButton
                  icon="i-lucide-trash-2"
                  variant="solid"
                  color="ruby"
                  size="sm"
                  @click="handleDeleteFailed"
                >
                  {{ t('LIBRARIES.DOCUMENTS.STATUS.DELETE_FAILED') }}
                </NextButton>
              </div>
            </div>
          </OnClickOutside>
        </div>
      </div>

      <!-- Pagination -->
      <div v-if="totalPages > 1" class="flex items-center space-x-2">
        <NextButton
          variant="ghost"
          size="sm"
          icon="i-lucide-chevrons-left"
          color="slate"
          class="!w-8 !h-6"
          :disabled="!canGoPrev"
          @click="handleFirstPage"
        />

        <NextButton
          variant="ghost"
          size="sm"
          icon="i-lucide-chevron-left"
          color="slate"
          class="!w-8 !h-6"
          :disabled="!canGoPrev"
          @click="handlePrevPage"
        />

        <div
          class="inline-flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300"
        >
          <span
            class="px-3 tabular-nums py-0.5 bg-slate-100 dark:bg-slate-800 rounded-md font-medium"
          >
            {{ currentPage }}
          </span>
          <span class="truncate">{{ pageInfo }}</span>
        </div>

        <NextButton
          variant="ghost"
          size="sm"
          icon="i-lucide-chevron-right"
          color="slate"
          class="!w-8 !h-6"
          :disabled="!canGoNext"
          @click="handleNextPage"
        />

        <NextButton
          variant="ghost"
          size="sm"
          icon="i-lucide-chevrons-right"
          color="slate"
          class="!w-8 !h-6"
          :disabled="!canGoNext"
          @click="handleLastPage"
        />
      </div>

      <div class="flex items-center text-sm text-slate-500 dark:text-slate-400">
        <span v-if="totalPages > 1">
          {{ currentPageInformation }}
        </span>
      </div>
    </div>

    <div v-if="showSchemaToggle" class="flex items-center">
      <SegmentedToggle
        :model-value="isSchemaActive"
        :options="schemaToggleOptions"
        size="small"
        @update:model-value="emit('toggleSchema')"
      />
    </div>
  </div>
</template>
